import { Schedule, Scale } from '@mui/icons-material';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { getJobDisplaySpecs } from '../../helpers/jobDisplay';

function ColorSwatch({ preview }) {
    const isTransparent = preview.css === 'transparent';

    return (
        <Tooltip title={preview.label} arrow>
            <Box
                aria-label={`Cor ${preview.label}`}
                sx={{
                    width: 18,
                    height: 18,
                    borderRadius: 0.75,
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: preview.needsBorder ? 'divider' : 'rgba(0,0,0,0.15)',
                    bgcolor: isTransparent ? 'transparent' : preview.css,
                    backgroundImage: isTransparent
                        ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                        : 'none',
                    backgroundSize: isTransparent ? '8px 8px' : 'auto',
                    backgroundPosition: isTransparent ? '0 0, 4px 4px' : 'initial',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                }}
            />
        </Tooltip>
    );
}

const FILAMENT_CHIP_COLORS = {
    PLA: { bg: 'rgba(34, 197, 94, 0.14)', color: '#15803d' },
    'PLA+': { bg: 'rgba(34, 197, 94, 0.2)', color: '#166534' },
    PETG: { bg: 'rgba(59, 130, 246, 0.14)', color: '#1d4ed8' },
    ABS: { bg: 'rgba(239, 68, 68, 0.12)', color: '#b91c1c' },
    TPU: { bg: 'rgba(168, 85, 247, 0.14)', color: '#7e22ce' },
    ASA: { bg: 'rgba(249, 115, 22, 0.14)', color: '#c2410c' },
};

function filamentChipSx(type) {
    const palette = FILAMENT_CHIP_COLORS[type] || {
        bg: 'rgba(148, 163, 184, 0.16)',
        color: 'text.secondary',
    };
    return {
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        bgcolor: palette.bg,
        color: palette.color,
        border: 'none',
    };
}

export default function JobListSpecs({ job, compact = false }) {
    const specs = getJobDisplaySpecs(job);

    if (!specs.hasSpecs) {
        return null;
    }

    return (
        <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            useFlexGap
            spacing={compact ? 0.5 : 0.75}
            sx={{ mt: compact ? 0.5 : 0.75 }}
        >
            {specs.durationLabel && (
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<Schedule sx={{ fontSize: '0.95rem !important' }} />}
                    label={specs.durationLabel}
                    sx={{ height: 24, '& .MuiChip-label': { px: 0.75 } }}
                />
            )}
            {specs.filamentLabel && (
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<Scale sx={{ fontSize: '0.95rem !important' }} />}
                    label={specs.filamentLabel}
                    sx={{ height: 24, '& .MuiChip-label': { px: 0.75 } }}
                />
            )}
            {specs.filamentType && (
                <Chip
                    size="small"
                    label={specs.filamentType}
                    sx={filamentChipSx(specs.filamentType)}
                />
            )}
            {specs.colors.length > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 0.25 }}>
                    {!compact && (
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
                            Cor
                        </Typography>
                    )}
                    {specs.colors.map((preview) => (
                        <ColorSwatch key={`${preview.css}-${preview.label}`} preview={preview} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
